import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { usePermission } from "../../../features/auth/use-permission";
import { HistoryTabs } from "../../../features/change-history/components/history-tabs";
import { RecordHistory } from "../../../features/change-history/components/record-history";
import { historyTabSearchSchema } from "../../../features/change-history/history-search";
import { practiceConfigurationsQueryOptions } from "../../../features/practice-configurations/api/practice-configurations";
import { PracticeConfigurationAdmin } from "../../../features/practice-configurations/components/practice-configuration-admin";

/**
 * `RPM-092`'s `practice_configurations` surfaces (`J-296`): the list, a setting's detail with a
 * locked key's reason, the edit, and each setting's History (`RPM-063`) — every value of its key at
 * its scope. A change asks `settings: ["update"]` at tenant scope, as the server does
 * (`practice-configurations.routes.ts`); a withheld change is stated in words.
 */
function PracticeConfigurationsScreen() {
  const { tab = "details" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const canChange = usePermission({ settings: ["update"] }, ["TENANT"]);
  const canReadHistory = usePermission({ audit: ["read"] }, ["TENANT"]);
  const settings = useQuery({ ...practiceConfigurationsQueryOptions(), enabled: canReadHistory });
  return (
    <div className="flex flex-col gap-4 pt-4">
      <HistoryTabs
        showHistory={canReadHistory}
        tab={tab}
        onTabChange={(next) =>
          void navigate({ search: (prev) => ({ ...prev, tab: next }), replace: true })
        }
        detailsLabel="Practice settings"
        details={<PracticeConfigurationAdmin canChange={canChange} />}
        history={
          <RecordHistory
            entity="practice-configurations"
            records={(settings.data ?? []).map((c) => ({
              id: c.configurationId,
              label: c.configKey,
            }))}
            noun="Setting"
            caption={(label) => `Change history of setting ${label}`}
            columnLabels={{ config_value: "Value", valid_from: "From", valid_to: "Until" }}
          />
        }
      />
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/admin/practice-configurations")({
  validateSearch: historyTabSearchSchema,
  component: PracticeConfigurationsScreen,
  staticData: { screenName: "Practice settings" },
});
